// PDF rasterization primitives for the client-PDF IMPORT flow.
//
// Turns an arbitrary client PDF (their finished cover / interior) into JPEG
// page images so the import can feed them into our NATIVE album model (cover →
// blankCoverImage; interior pages → 1-up photo slots). The album then renders
// through the exact same cover composer + interior pipeline a normal order uses
// — our locked geometry, branding, intro/outro and 3D preview all come for free.
//
// pdfjs is the same dependency previewAssets.ts already rasterizes the print
// PDFs with; imported dynamically so it stays out of the main editor bundle
// until an admin actually imports a PDF.

import { detectEdgeBandsNested, hasBands, keepRect, rgbaToLuma, type EdgeBands } from './edgeBandTrim';

export interface RasterPage {
  blob: Blob;
  width: number;
  height: number;
}

async function loadPdfjs() {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.js?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  }
  return pdfjs;
}

async function renderPageToCanvas(
  page: import('pdfjs-dist').PDFPageProxy,
  longEdgePx: number,
): Promise<HTMLCanvasElement> {
  const base = page.getViewport({ scale: 1 });
  const scale = longEdgePx / Math.max(base.width, base.height);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d', { alpha: false })!;
  // White backing so any transparency flattens to white (print paper), not black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Crop a horizontal sub-region [x0,x1] (fractions 0..1 of width, full height)
 *  out of a canvas — used to slice a full-wrap cover page into back / front. */
function cropHorizontalFraction(
  source: HTMLCanvasElement,
  x0: number,
  x1: number,
): HTMLCanvasElement {
  const sx = Math.round(Math.max(0, Math.min(1, x0)) * source.width);
  const sw = Math.round((Math.max(0, Math.min(1, x1)) - Math.max(0, Math.min(1, x0))) * source.width);
  const c = document.createElement('canvas');
  c.width = Math.max(1, sw);
  c.height = source.height;
  c.getContext('2d', { alpha: false })!.drawImage(source, sx, 0, c.width, c.height, 0, 0, c.width, c.height);
  return c;
}

/** Page count of a client PDF (cheap — no rendering). */
export async function pdfPageCount(bytes: Uint8Array): Promise<number> {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    return doc.numPages;
  } finally {
    await doc.destroy();
  }
}

/** Physical size (mm) of one page of a client PDF — used to decide whether an
 *  imported cover already matches our sheet spec (keep as-is) or must be refit. */
export async function pdfPageSizeMm(bytes: Uint8Array, pageIndex = 0): Promise<{ wmm: number; hmm: number }> {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const vp = (await doc.getPage(pageIndex + 1)).getViewport({ scale: 1 }); // points (72 dpi)
    return { wmm: (vp.width / 72) * 25.4, hmm: (vp.height / 72) * 25.4 };
  } finally {
    await doc.destroy();
  }
}

/** Rasterize EVERY page (or a chosen subset) of a client PDF to print-grade
 *  JPEGs. Pages render sequentially to keep peak memory at ~one canvas. */
export async function rasterizePdfPages(
  bytes: Uint8Array,
  opts: {
    longEdgePx?: number;
    quality?: number;
    pageIndices?: number[]; // 0-based; default = all pages in order
    onProgress?: (current: number, total: number) => void;
  } = {},
): Promise<RasterPage[]> {
  const longEdgePx = opts.longEdgePx ?? 3300; // ≈ 282 DPI on an A4 long edge
  const quality = opts.quality ?? 0.9;
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  const out: RasterPage[] = [];
  try {
    const indices = opts.pageIndices ?? Array.from({ length: doc.numPages }, (_, i) => i);
    const total = indices.length;
    for (let i = 0; i < indices.length; i++) {
      const pageNo = indices[i] + 1;
      if (pageNo < 1 || pageNo > doc.numPages) continue;
      opts.onProgress?.(i, total);
      const page = await doc.getPage(pageNo);
      const canvas = await renderPageToCanvas(page, longEdgePx);
      out.push({ blob: await canvasToJpeg(canvas, quality), width: canvas.width, height: canvas.height });
      page.cleanup();
    }
    opts.onProgress?.(total, total);
  } finally {
    await doc.destroy();
  }
  return out;
}

/**
 * Rasterise every page, handing each one to `onPage` as soon as it is ready.
 *
 * The PDF is parsed ONCE. `rasterizePdfPages` returns them all at the end,
 * which for a 77 MB, 100-page customer PDF means holding a hundred 3600 px
 * JPEGs at once; calling it per page instead re-parses the document a hundred
 * times. This does neither: one parse, one page in memory, and the caller
 * ships it before the next is drawn. Built for the WhatsApp PDF intake
 * (pages/internal/PdfIntakeProbe.tsx). A throwing `onPage` stops the run.
 */
export async function forEachPdfPage(
  bytes: Uint8Array,
  opts: { longEdgePx?: number; quality?: number },
  onPage: (page: RasterPage, index: number, total: number) => Promise<void>,
): Promise<number> {
  const longEdgePx = opts.longEdgePx ?? 3300;
  const quality = opts.quality ?? 0.9;
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const total = doc.numPages;
    for (let i = 0; i < total; i++) {
      const page = await doc.getPage(i + 1);
      const canvas = await renderPageToCanvas(page, longEdgePx);
      const raster = { blob: await canvasToJpeg(canvas, quality), width: canvas.width, height: canvas.height };
      page.cleanup();
      // Free the canvas before the upload, not after — it is the big object.
      canvas.width = 0; canvas.height = 0;
      await onPage(raster, i, total);
    }
    return total;
  } finally {
    await doc.destroy();
  }
}

/** Slice ONE full-wrap cover page (front+spine+back at the client's own
 *  dimensions) into a front image and a back image, cropping by fractions of
 *  the page width. The wrap spread is [back | spine | front] left→right, so the
 *  back is the left region and the front is the right region; the spine strip
 *  between them is dropped (OUR spine column + colour fill owns it).
 *
 *  @param backRight  fraction (0..1) where the back region ENDS
 *  @param frontLeft  fraction (0..1) where the front region BEGINS
 */
export async function rasterizeWrapToFrontBack(
  bytes: Uint8Array,
  pageIndex: number,
  cut: { backRight: number; frontLeft: number },
  opts: { longEdgePx?: number; quality?: number } = {},
): Promise<{ front: RasterPage; back: RasterPage }> {
  const quality = opts.quality ?? 0.9;
  // Render the whole wrap big enough that the front region lands at print grade:
  // if the front is ~40% of the wrap width, render the wrap ~2.5× larger.
  const frontFrac = Math.max(0.1, 1 - cut.frontLeft);
  const wrapLongEdge = Math.round((opts.longEdgePx ?? 3300) / frontFrac);
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const page = await doc.getPage(pageIndex + 1);
    const wrap = await renderPageToCanvas(page, wrapLongEdge);
    const backC = cropHorizontalFraction(wrap, 0, cut.backRight);
    const frontC = cropHorizontalFraction(wrap, cut.frontLeft, 1);
    page.cleanup();
    return {
      front: { blob: await canvasToJpeg(frontC, quality), width: frontC.width, height: frontC.height },
      back: { blob: await canvasToJpeg(backC, quality), width: backC.width, height: backC.height },
    };
  } finally {
    await doc.destroy();
  }
}

/** Cover-fit a SUB-RECT of a source canvas into a destination rect (preserves
 *  aspect, crops overflow) — the same max-scale fill the cover PDF uses. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: HTMLCanvasElement,
  sx: number, sy: number, sw: number, sh: number,
  dx: number, dy: number, dw: number, dh: number,
) {
  const srcAR = sw / sh, dstAR = dw / dh;
  if (srcAR > dstAR) { const cw = sh * dstAR; sx += (sw - cw) / 2; sw = cw; }
  else { const ch = sw / dstAR; sy += (sh - ch) / 2; sh = ch; }
  ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
}

function sampleBgColor(src: HTMLCanvasElement): string {
  try {
    const d = src.getContext('2d')!.getImageData(2, Math.floor(src.height / 2), 1, 1).data;
    return `rgb(${d[0]},${d[1]},${d[2]})`;
  } catch { return '#f3efe6'; }
}

/**
 * Composite a client cover into OUR sheet layout so it fills the whole cover as
 * one continuous piece: client front → our front A4 panel, spine → our spine
 * column, back → our back A4 panel (matching generateCoverPdf's placement). The
 * turn-in margins get a sampled background colour (they fold under the board).
 * The result is drawn 1:1 to fill the sheet by the cover generator, with our
 * logo + marry-label overlaid.
 */
export async function composeCoverWrapImage(opts: {
  kase: 'A' | 'B' | 'C';
  bytes: Uint8Array;
  frontIndex: number;
  backIndex: number;
  wrapCut: { backRight: number; frontLeft: number };
  sheetWmm: number;
  sheetHmm: number;
  spineColMm: number;
  longEdgePx?: number;
  /** The client's wrap ALREADY matches our sheet spec → keep it EXACTLY: draw
   *  it 1:1 to fill the sheet, no slicing/refit. Our logo + order-id overlay it
   *  later in the cover generator. */
  passthrough?: boolean;
}): Promise<{ blob: Blob; width: number; height: number }> {
  const { kase, bytes, sheetWmm, sheetHmm, spineColMm } = opts;
  const W = opts.longEdgePx ?? 5000;
  const H = Math.round(W * sheetHmm / sheetWmm);
  const MMPX = W / sheetWmm;
  const A4W = 210 * MMPX, A4H = 297 * MMPX;
  const txMm = (sheetWmm - (2 * 210 + spineColMm)) / 2;
  const tyMm = (sheetHmm - 297) / 2;
  const backX = txMm * MMPX;
  const spineX = backX + A4W;
  const frontX = spineX + spineColMm * MMPX;
  const panelY = tyMm * MMPX;

  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d', { alpha: false })!;

  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    if (opts.passthrough) {
      // Already our spec — fill the whole sheet 1:1 (client wrap AR == sheet AR),
      // no slicing, so nothing shifts. High-res render for a crisp cover.
      const wrap = await renderPageToCanvas(await doc.getPage((opts.frontIndex >= 0 ? opts.frontIndex : 0) + 1), Math.max(W, 6000));
      drawCover(ctx, wrap, 0, 0, wrap.width, wrap.height, 0, 0, W, H);
    } else if (kase === 'A') {
      const wrap = await renderPageToCanvas(await doc.getPage((opts.frontIndex >= 0 ? opts.frontIndex : 0) + 1), Math.max(W, 5200));
      ctx.fillStyle = sampleBgColor(wrap);
      ctx.fillRect(0, 0, W, H);
      const sw = wrap.width, sh = wrap.height;
      const br = Math.max(0.05, Math.min(0.95, opts.wrapCut.backRight));
      const fl = Math.max(br, Math.min(0.98, opts.wrapCut.frontLeft));
      // client back region → our back panel
      drawCover(ctx, wrap, 0, 0, br * sw, sh, backX, panelY, A4W, A4H);
      // client spine strip → our spine column
      drawCover(ctx, wrap, br * sw, 0, (fl - br) * sw, sh, spineX, panelY, spineColMm * MMPX, A4H);
      // client front region → our front panel
      drawCover(ctx, wrap, fl * sw, 0, (1 - fl) * sw, sh, frontX, panelY, A4W, A4H);
    } else {
      const front = await renderPageToCanvas(await doc.getPage(opts.frontIndex + 1), 3200);
      ctx.fillStyle = sampleBgColor(front);
      ctx.fillRect(0, 0, W, H);
      drawCover(ctx, front, 0, 0, front.width, front.height, frontX, panelY, A4W, A4H);
      if (kase === 'B' && opts.backIndex >= 0) {
        const back = await renderPageToCanvas(await doc.getPage(opts.backIndex + 1), 3200);
        drawCover(ctx, back, 0, 0, back.width, back.height, backX, panelY, A4W, A4H);
      }
      // spine column (+ back for case C) stay the sampled background colour.
    }
  } finally {
    await doc.destroy();
  }
  const blob = await canvasToJpeg(canvas, 0.92);
  return { blob, width: W, height: H };
}

/** Rotate a canvas 90° clockwise (landscape → portrait). */
function rotate90(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = src.height; c.height = src.width;
  const ctx = c.getContext('2d', { alpha: false })!;
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  return c;
}

/** How far a page may miss the trim's shape and still be laid on the sheet
 *  whole (the gap is filled from its own edges): 1 mm a side. Past that the
 *  fill shows on the printed page as a doubled strip — #1247. */
export const SHEET_FILL_MAX_MM = 1;

export interface InteriorFitPlan {
  /** 'sheet' = whole page inside the trim, ≤ 1 mm filled, + bleed ring;
   *  'native' = the page alone at its own shape, cover-fitted by the slot. */
  mode: 'sheet' | 'native';
  outW: number;
  outH: number;
  /** Fit-inside letterbox inside the trim, per side, in mm. */
  gapMm: { x: number; y: number };
}

/**
 * Decide how one (bleed-trimmed, portrait) client page becomes a photo.
 *
 * Up to 2026-10-18 every page was laid WHOLE inside the 200×297 trim and the
 * letterbox filled with a cover-fit — i.e. ZOOMED — copy of the same page. On
 * #1247 (3:4 phone photos as PDF pages) that letterbox was 15 mm top and
 * bottom: every photo printed with a doubled strip (her trousers twice at the
 * bottom). A filled gap is only invisible inside the bleed, so:
 *   · the page fits the trim to within SHEET_FILL_MAX_MM → as before (a
 *     finished A4/trim-sized page keeps every millimetre of its design);
 *   · otherwise → the page ALONE, at its own shape, sized to cover the sheet
 *     at 300 DPI. The 1-up slot cover-fits it like any photo: a little of the
 *     long side is cropped (≈ 4.5 mm a side for 3:4) instead of a fake strip
 *     being printed, and the editor can still pan it.
 */
export function planInteriorFit(
  srcW: number, srcH: number,
  opts: { trimWmm?: number; trimHmm?: number; bleedMm?: number; dpi?: number } = {},
): InteriorFitPlan {
  const trimWmm = opts.trimWmm ?? 200;
  const trimHmm = opts.trimHmm ?? 297;
  const bleedMm = opts.bleedMm ?? 3;
  const dpi = opts.dpi ?? 300;
  const fullWmm = trimWmm + 2 * bleedMm;
  const fullHmm = trimHmm + 2 * bleedMm;
  const sheetW = Math.round((fullWmm * dpi) / 25.4); // 206mm → 2433px
  const sheetH = Math.round((fullHmm * dpi) / 25.4); // 303mm → 3579px
  const fit = Math.min(trimWmm / srcW, trimHmm / srcH); // mm per source px
  const gapMm = { x: (trimWmm - srcW * fit) / 2, y: (trimHmm - srcH * fit) / 2 };
  if (Math.max(gapMm.x, gapMm.y) <= SHEET_FILL_MAX_MM) {
    return { mode: 'sheet', outW: sheetW, outH: sheetH, gapMm };
  }
  const cover = Math.max(sheetW / srcW, sheetH / srcH);
  return { mode: 'native', outW: Math.round(srcW * cover), outH: Math.round(srcH * cover), gapMm };
}

/** Detection runs on a copy this size (long edge): seams are found to ±1 px
 *  of it, i.e. ±0.07 % of the page. */
const BLEED_DETECT_LONG_EDGE = 1400;

/**
 * Cut a print bleed the CLIENT's tool made by repeating / mirroring / smearing
 * the photo's edge (lib/edgeBandTrim). Strong evidence only: no proof, no cut.
 * Exported for the import tests; returns the source unchanged when nothing is
 * found.
 */
export function trimClientBleed(src: HTMLCanvasElement): { canvas: HTMLCanvasElement; bands: EdgeBands | null } {
  try {
    const k = Math.min(1, BLEED_DETECT_LONG_EDGE / Math.max(src.width, src.height));
    const w = Math.max(1, Math.round(src.width * k)), h = Math.max(1, Math.round(src.height * k));
    const small = document.createElement('canvas');
    small.width = w; small.height = h;
    const sctx = small.getContext('2d', { alpha: false })!;
    sctx.drawImage(src, 0, 0, w, h);
    const bands = detectEdgeBandsNested(rgbaToLuma(sctx.getImageData(0, 0, w, h).data, w, h));
    if (!hasBands(bands)) return { canvas: src, bands: null };
    const r = keepRect(src.width, src.height, bands);
    const out = document.createElement('canvas');
    out.width = r.width; out.height = r.height;
    out.getContext('2d', { alpha: false })!.drawImage(src, r.x, r.y, r.width, r.height, 0, 0, r.width, r.height);
    return { canvas: out, bands };
  } catch {
    // Detection is a refinement; a page that cannot be read back (tainted or
    // zero-size canvas) is imported exactly as before.
    return { canvas: src, bands: null };
  }
}

/**
 * Turn ONE client interior page into a print-grade photo (see planInteriorFit
 * for the shape it takes). Landscape pages are auto-rotated to portrait first,
 * and a bleed the client's own tool painted is cut off before anything else.
 * Full print resolution, no face crop.
 */
export async function composeInteriorPageImage(
  bytes: Uint8Array,
  pageIndex: number,
  opts: { trimWmm?: number; trimHmm?: number; bleedMm?: number } = {},
): Promise<RasterPage> {
  const trimWmm = opts.trimWmm ?? 200;
  const trimHmm = opts.trimHmm ?? 297;
  const bleedMm = opts.bleedMm ?? 3;

  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const sheetH = Math.round(((trimHmm + 2 * bleedMm) * 300) / 25.4);
    let src = await renderPageToCanvas(await doc.getPage(pageIndex + 1), Math.max(sheetH, 3600));
    if (src.width > src.height) src = rotate90(src); // landscape → portrait
    src = trimClientBleed(src).canvas;

    const plan = planInteriorFit(src.width, src.height, { trimWmm, trimHmm, bleedMm });
    const canvas = document.createElement('canvas');
    canvas.width = plan.outW; canvas.height = plan.outH;
    const ctx = canvas.getContext('2d', { alpha: false })!;
    if (plan.mode === 'native') {
      // The page alone — nothing painted that the client did not send.
      ctx.drawImage(src, 0, 0, plan.outW, plan.outH);
    } else {
      // Background: cover-fit the whole sheet so the bleed ring (+ ≤ 1 mm of
      // trim) is filled with the page's own edges, never white. Only that thin
      // ring ever shows the fill, and the trim cuts most of it away.
      drawCover(ctx, src, 0, 0, src.width, src.height, 0, 0, plan.outW, plan.outH);
      // Foreground: the WHOLE page, fit inside the trim rect (no crop into faces).
      const fullWmm = trimWmm + 2 * bleedMm, fullHmm = trimHmm + 2 * bleedMm;
      const bx = (bleedMm / fullWmm) * plan.outW;
      const by = (bleedMm / fullHmm) * plan.outH;
      const tw = plan.outW - 2 * bx, th = plan.outH - 2 * by;
      const s = Math.min(tw / src.width, th / src.height);
      const dw = src.width * s, dh = src.height * s;
      ctx.drawImage(src, bx + (tw - dw) / 2, by + (th - dh) / 2, dw, dh);
    }

    return { blob: await canvasToJpeg(canvas, 0.92), width: plan.outW, height: plan.outH };
  } finally {
    await doc.destroy();
  }
}

/** Small data-URI thumbnail of a single client PDF page, for the confirm UI. */
export async function pdfPageThumbnail(bytes: Uint8Array, pageIndex: number, longEdgePx = 220): Promise<string> {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const page = await doc.getPage(pageIndex + 1);
    const canvas = await renderPageToCanvas(page, longEdgePx);
    page.cleanup();
    return canvas.toDataURL('image/jpeg', 0.7);
  } finally {
    await doc.destroy();
  }
}
